import type { BackgroundCheck, ScreeningProviderConfiguration } from "@repo/models";
import type { GridList } from "@repo/ui";
import type { AxiosRequestConfig } from "axios";

import { BaseController, BaseGridApi } from "@repo/baseCloud";

import type { BackgroundChecksFilter, ConfigurationsFilter } from "../pages/backgroundChecks/types";

export const controller = "BackgroundChecks";

export default class BackgroundChecksApi {
	static Get = BaseController.GetFn(controller);
	static Post = BaseController.PostFn(controller);

	/** Loads all background checks matching filters */
	static getBackgroundChecks(filter: BackgroundChecksFilter, config?: AxiosRequestConfig) {
		return BackgroundChecksApi.Get<GridList<BackgroundCheck>>("GetBackgroundChecks", filter, config);
	}

	static downloadBulkUploadTemplate() {
		return BackgroundChecksApi.Get<Blob>("DownloadBulkUploadTemplate", {}, { responseType: "blob" });
	}

	/** Process bulk import file */
	static processBulkImportFile(data: FormData) {
		return BackgroundChecksApi.Post<{ importLog: string; errorLog?: string }>("ProcessBulkImportFile", {}, data);
	}

	/** Loads screening providers for filtering */
	static getScreeningProviders(isIntegrated?: boolean) {
		return BackgroundChecksApi.Get<{ ScreeningProviderId: number; Name: string }[]>("GetScreeningProviders", {
			isIntegrated,
		});
	}

	/** Loads screening products */
	static getScreeningProducts() {
		return BackgroundChecksApi.Get<{ ScreeningProductId: number; Name: string }[]>("GetScreeningProducts");
	}

	/** Loads configurations for grid */
	static getConfigurations(filter: ConfigurationsFilter, config?: AxiosRequestConfig) {
		return BackgroundChecksApi.Get<GridList<ScreeningProviderConfiguration>>("GetConfigurations", filter, config);
	}

	/** Saves configuration */
	static saveConfiguration(data: Partial<ScreeningProviderConfiguration>) {
		return BackgroundChecksApi.Post<void>("SaveConfiguration", {}, data);
	}

	/** Tests connection. Resolves empty when the provider accepts the credentials, or with the error message. */
	static testConfiguration(data: Partial<ScreeningProviderConfiguration>) {
		return BackgroundChecksApi.Post<string | null>("TestConnection", {}, data);
	}

	/** Loads saved grid settings for background checks (BackgroundChecksController.GridName) */
	static loadGridSettings() {
		return BaseGridApi.loadGridSettings(controller, "BackgroundChecks");
	}

	/** Saves grid settings for background checks */
	static saveGridSettings(settingsJson: string) {
		return BaseGridApi.saveGridSettings(controller, "BackgroundChecks", settingsJson);
	}

	static getGridColumns() {
		return BaseGridApi.getGridColumns<any>(controller, "BackgroundCheck");
	}
}
